/**
 * hooks/useEventStream.ts — live Kalshi orderbook + fills over the router's
 * /ws/events Durable Object (KALSHI_STREAM). STRICTLY ADDITIVE: this is an
 * enhancement over the REST orderbook poll in EventsTab. The REST query stays
 * in place as the authoritative source; this hook only makes the visible book
 * refresh faster while the upstream relay is healthy, and reports `live=false`
 * (or emits a resync) the instant it is not — at which point the component
 * simply keeps rendering the REST book. Nothing here can blank the UI.
 *
 * BYOK: the Kalshi API key + RSA PEM are the SAME creds the vault already sends
 * as x-event-* headers on the REST portfolio/order calls. Here they travel once,
 * over the established wss channel (TLS), in an `auth` message body — never in
 * the URL. The router's KalshiStreamDO holds them in memory only, signs the one
 * upstream handshake, and drops them when idle.
 *
 * Protocol (see router/src/do/kalshiStreamDO.ts):
 *   client → { op:'auth', apiKeyId, privateKey, tickers:[T] }
 *   client → { op:'subscribe' | 'unsubscribe', tickers:[...] }
 *   client → { op:'ping' }
 *   server → { type:'orderbook', ticker, book:{ venue, ticker, yes:[], no:[] } }
 *   server → { type:'fill', ticker, side, action, count, price, ts }
 *   server → { type:'resync', ticker, reason }   // seq gap → trust REST
 *   server → { type:'status', payload:{ upstream, mode, ... } }
 *   server → { type:'pong' }
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { mintWsTicket, wsUrlWithTicket } from '@/lib/wsTicket';
import { useStore } from '@/store';

interface OBLevel { price: number; count: number }
interface Orderbook { venue: string; ticker: string; yes: OBLevel[]; no: OBLevel[] }

interface EventStreamCreds { apiKeyId: string; privateKey: string }
interface UseEventStreamArgs {
  venue: string;
  ticker: string | null;
  mode: 'demo' | 'prod';
  creds: EventStreamCreds | null;
  enabled?: boolean;
  onFill?: () => void;   // e.g. refetch portfolio when one of our orders fills
}
export interface EventFill {
  ticker: string; side: string; action: string;
  count: number; price: number | null; ts: number;
}
export interface EventStreamState {
  connected: boolean;    // downstream socket to our router is open
  live: boolean;         // upstream Kalshi relay is open and delivering
  book: Orderbook | null;// latest live book for `ticker` (null → use REST)
  fills: EventFill[];    // most-recent-first, capped; streamed from the relay
}

function wsEventsBase(): string {
  const apiBase = import.meta.env.VITE_API_BASE as string | undefined;
  const override = import.meta.env.VITE_WS_URL as string | undefined;
  if (override) return override.replace(/\/ws$/, '') + '/ws/events';
  if (!apiBase) return 'wss://api.omnistrat.ai/ws/events';
  return apiBase.replace(/^http/, 'ws').replace(/\/?$/, '') + '/ws/events';
}

// One single-use ticket per connect (lib/wsTicket.ts); NO ?token= fallback --
// the router refuses it on /ws/events (401 TOKEN_IN_URL_REFUSED).
const postTicket = () => api<unknown>('/ws/ticket', { method: 'POST', body: JSON.stringify({}) });

const BACKOFF = [1000, 2000, 4000, 8000, 16000, 30000];
const PING_INTERVAL_MS = 15000;

export function useEventStream(args: UseEventStreamArgs): EventStreamState {
  const { venue, ticker, mode, creds, enabled = true, onFill } = args;
  // Only Kalshi has a live relay today; Polymarket US stays REST-only.
  const active = enabled && venue === 'kalshi' && !!creds && !!ticker;

  const [state, setState] = useState<EventStreamState>({ connected: false, live: false, book: null, fills: [] });

  const wsRef        = useRef<WebSocket | null>(null);
  const mountedRef   = useRef(true);
  const attemptRef   = useRef(0);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickerRef    = useRef<string | null>(ticker);
  const credsRef     = useRef<EventStreamCreds | null>(creds);
  const onFillRef    = useRef(onFill);
  tickerRef.current  = ticker;
  credsRef.current   = creds;
  onFillRef.current  = onFill;

  // Re-subscribe when the focused ticker changes on an open socket.
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    // Fresh book for the new ticker; drop the stale one so the UI shows REST
    // until the first live snapshot for the new ticker arrives.
    setState(s => ({ ...s, book: null }));
    try {
      if (tickerRef.current) ws.send(JSON.stringify({ op: 'subscribe', tickers: [tickerRef.current] }));
    } catch { /* onclose reconnect will resubscribe */ }
  }, [ticker]);

  useEffect(() => {
    mountedRef.current = true;

    if (!active) {
      // Tear down any existing socket when the stream shouldn't run (locked
      // vault, non-Kalshi venue, no selection). The component falls back to
      // REST automatically because `book` is null.
      try { wsRef.current?.close(1000, 'inactive'); } catch { /* noop */ }
      wsRef.current = null;
      setState({ connected: false, live: false, book: null, fills: [] });
      return () => { mountedRef.current = false; };
    }

    async function connect() {
      if (!mountedRef.current) return;
      const token = useStore.getState().token as string | undefined;
      if (!token) return;   // not signed in: REST only
      // Fresh single-use ticket for every connect and reconnect.
      const t = await mintWsTicket(postTicket);
      if (!mountedRef.current) return;
      if (!t.ok) {
        // The component falls back to REST (book stays null). Fatal (401/403)
        // stops here; anything else backs off and mints again.
        if (t.fatal) return;
        const delay = BACKOFF[Math.min(attemptRef.current, BACKOFF.length - 1)];
        attemptRef.current++;
        reconnectRef.current = setTimeout(connect, delay);
        return;
      }
      const url = wsUrlWithTicket(wsEventsBase(), t.ticket, { mode });
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!mountedRef.current) return;
          attemptRef.current = 0;
          setState(s => ({ ...s, connected: true }));
          const c = credsRef.current;
          // Send creds + initial subscription in one auth message.
          if (c && tickerRef.current) {
            ws.send(JSON.stringify({ op: 'auth', apiKeyId: c.apiKeyId, privateKey: c.privateKey, tickers: [tickerRef.current] }));
          }
          pingRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) { try { ws.send(JSON.stringify({ op: 'ping' })); } catch { /* noop */ } }
          }, PING_INTERVAL_MS);
        };

        ws.onmessage = (ev) => {
          if (!mountedRef.current) return;
          let msg: {
            type?: string; ticker?: string; book?: Orderbook;
            side?: string; action?: string; count?: number; price?: number | null; ts?: number;
            payload?: { upstream?: string };
          };
          try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : ''); } catch { return; }
          switch (msg.type) {
            case 'orderbook':
              // Only apply the book for the ticker we're currently showing.
              if (msg.book && msg.ticker && msg.ticker.toUpperCase() === (tickerRef.current ?? '').toUpperCase()) {
                setState(s => ({ ...s, live: true, book: msg.book! }));
              }
              break;
            case 'resync':
              // Upstream told us our delta book is untrustworthy. Drop it and
              // let REST be the source of truth again.
              setState(s => ({ ...s, book: null }));
              break;
            case 'fill': {
              if (msg.ticker) {
                const fill: EventFill = {
                  ticker: msg.ticker,
                  side: msg.side ?? '', action: msg.action ?? '',
                  count: Number(msg.count ?? 0),
                  price: typeof msg.price === 'number' ? msg.price : null,
                  ts: typeof msg.ts === 'number' ? msg.ts : Date.now(),
                };
                setState(s => ({ ...s, fills: [fill, ...s.fills].slice(0, 25) }));
              }
              onFillRef.current?.();
              break;
            }
            case 'status': {
              const up = msg.payload?.upstream;
              setState(s => ({ ...s, live: up === 'open', ...(up !== 'open' ? { book: null } : {}) }));
              break;
            }
            // 'pong' — no-op; presence of traffic is enough.
          }
        };

        ws.onclose = (ev) => {
          if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
          setState(s => ({ ...s, connected: false, live: false, book: null }));
          if (!mountedRef.current || ev.code === 1000) return;
          const delay = BACKOFF[Math.min(attemptRef.current, BACKOFF.length - 1)];
          attemptRef.current++;
          reconnectRef.current = setTimeout(connect, delay);
        };
        ws.onerror = () => { try { ws.close(); } catch { /* onclose handles reconnect */ } };
      } catch {
        const delay = BACKOFF[Math.min(attemptRef.current, BACKOFF.length - 1)];
        attemptRef.current++;
        reconnectRef.current = setTimeout(connect, delay);
      }
    }

    void connect();
    return () => {
      mountedRef.current = false;
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
      if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
      try { wsRef.current?.close(1000, 'unmount'); } catch { /* noop */ }
      wsRef.current = null;
    };
  // Reconnect when activation, venue, mode or the api-key identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, venue, mode, creds?.apiKeyId]);

  return state;
}
